// ─── Methodological Restraint Engine ─────────────────────────────────────────
// Prevents "AI econometric overproduction" — the tendency to run every possible
// analysis regardless of theoretical justification or analytical appropriateness.
// Returns a curated pipeline recommendation based on domain, sample, and design.

import { getDomainProfile } from './domainRealismProfiles'

// ── Analysis suitability rules ─────────────────────────────────────────────────

const ANALYSIS_RULES = {
  descriptive: {
    label: 'Descriptive Statistics',
    alwaysInclude: true,
    minN: 1,
    justification: 'Always required to characterise the sample and identify data quality issues.',
  },
  correlation: {
    label: 'Bivariate Correlation Matrix',
    alwaysInclude: true,
    minN: 10,
    justification: 'Preliminary bivariate analysis is expected before regression. Identifies multicollinearity.',
  },
  ols: {
    label: 'OLS Regression',
    alwaysInclude: false,
    minN: 30,
    requiresDV: true,
    minIVs: 2,
    justification: 'OLS is appropriate when DV is continuous and predictors are theoretically motivated.',
    inappropriate: {
      binary_dv: 'Use logistic regression for binary outcomes — OLS produces probability estimates outside [0,1].',
      small_n: 'OLS with n < 30 and multiple predictors is severely underpowered.',
    },
  },
  cronbach: {
    label: "Cronbach's Alpha",
    alwaysInclude: false,
    minN: 30,
    requiresLikert: true,
    minItems: 2,
    justification: 'Run only if the study uses multi-item Likert scales measuring a latent construct.',
    inappropriate: {
      single_item: 'Cannot compute alpha for single-item measures.',
      mixed_types: 'Alpha should not pool continuous and Likert items.',
    },
  },
  anova: {
    label: 'One-Way ANOVA',
    alwaysInclude: false,
    minN: 30,
    requiresGroupVar: true,
    justification: 'Appropriate when comparing group means on a continuous DV (e.g., sector, region).',
  },
  moderation: {
    label: 'Moderation Analysis',
    alwaysInclude: false,
    minN: 80,
    requiresTheory: true,
    justification: 'Run only when a specific theoretically grounded moderating hypothesis is stated.',
    inappropriate: {
      no_theory: 'Moderation analysis without prior theoretical motivation constitutes data dredging.',
      small_n: 'Detecting interaction effects requires substantially larger samples (n > 80 minimum).',
    },
  },
  factor: {
    label: 'Exploratory Factor Analysis',
    alwaysInclude: false,
    minN: 100,
    requiresLikert: true,
    minItems: 5,
    justification: 'Appropriate only for scale development studies with 5+ items per latent factor.',
    inappropriate: {
      small_n: 'EFA requires n ≥ 100 (ideally 10× items). Results are unstable at smaller samples.',
      small_items: 'EFA with fewer than 5 items per expected factor is methodologically indefensible.',
      confirmatory: 'If a factor structure is theoretically expected, use CFA, not EFA.',
    },
  },
}

// ── Sophistication matrix ─────────────────────────────────────────────────────

const SOPHISTICATION_LEVELS = {
  basic:    { label: 'Basic',    analyses: ['descriptive', 'correlation'] },
  moderate: { label: 'Moderate', analyses: ['descriptive', 'correlation', 'ols', 'cronbach', 'anova'] },
  advanced: { label: 'Advanced', analyses: ['descriptive', 'correlation', 'ols', 'cronbach', 'anova', 'moderation'] },
}

// ── Main export ────────────────────────────────────────────────────────────────

/**
 * Recommend a methodologically appropriate analysis pipeline.
 *
 * @param {{ domain, sampleSize, variables, thesisType }} config
 * @returns {MethodologicalRecommendation}
 */
export function recommendPipeline(config) {
  const { domain, sampleSize, variables, thesisType } = config
  const profile = getDomainProfile(domain)
  const maxSophLevel = profile.analysisSophistication
  const allowedByLevel = SOPHISTICATION_LEVELS[maxSophLevel]?.analyses || SOPHISTICATION_LEVELS.moderate.analyses

  const dvVars  = variables.filter(v => v.role === 'dv')
  const ivVars  = variables.filter(v => v.role === 'iv')
  const likertVars = variables.filter(v => v.type === 'likert5' || v.type === 'likert7')
  const hasDV = dvVars.length > 0
  const hasGroupVar = variables.some(v => v.type === 'binary' && v.role === 'control')

  const recommendations = []
  const warnings = []
  const justifications = {}

  for (const [key, rule] of Object.entries(ANALYSIS_RULES)) {
    // Skip if not allowed by domain sophistication level
    if (!rule.alwaysInclude && !allowedByLevel.includes(key)) {
      warnings.push({
        analysis: key,
        reason: `${rule.label} is above the recommended sophistication level for ${profile.label} research. Including it risks appearing methodologically overambitious.`,
      })
      continue
    }

    // Check minimum N
    if (sampleSize < rule.minN) {
      warnings.push({
        analysis: key,
        reason: `${rule.label} requires n ≥ ${rule.minN}. Your sample (n = ${sampleSize}) is too small. ${rule.inappropriate?.small_n || ''}`,
      })
      continue
    }

    // Check specific requirements
    if (rule.requiresDV && !hasDV) continue
    if (rule.requiresLikert && likertVars.length < (rule.minItems || 2)) {
      if (key === 'cronbach' || key === 'factor') {
        warnings.push({
          analysis: key,
          reason: `${rule.label} requires at least ${rule.minItems || 2} Likert-type items. ${rule.inappropriate?.small_items || ''}`,
        })
        continue
      }
    }
    if (key === 'ols' && ivVars.length < (rule.minIVs || 2)) {
      warnings.push({ analysis: key, reason: 'OLS regression requires at least 2 independent variables.' })
      continue
    }
    if (key === 'moderation' && !config.hasModeratorHypothesis) {
      warnings.push({
        analysis: key,
        reason: `${rule.inappropriate?.no_theory} State the specific interaction hypothesis before running moderation.`,
      })
      continue
    }
    if (key === 'anova' && !hasGroupVar && !config.hasGroupVar) continue

    recommendations.push(key)
    justifications[key] = rule.justification
  }

  // Overproduction check
  const overproductionWarnings = []
  if (recommendations.length > 6) {
    overproductionWarnings.push('Running more than 5–6 analysis types risks appearing methodologically unfocused. Consider removing analyses that lack explicit theoretical motivation.')
  }
  if (recommendations.includes('moderation') && recommendations.includes('anova') && sampleSize < 200) {
    overproductionWarnings.push('Running both moderation and ANOVA on a small sample produces underpowered, difficult-to-interpret results. Prioritise the one aligned with your primary research question.')
  }
  if (thesisType === 'dba' && recommendations.includes('factor')) {
    overproductionWarnings.push('DBA theses prioritise managerial interpretation. Factor analysis is typically reserved for academic MPhil/PhD scale development work.')
  }

  return {
    recommendedAnalyses: recommendations,
    sophisticationLevel: maxSophLevel,
    justifications,
    exclusionWarnings: warnings,
    overproductionWarnings,
    note: overproductionWarnings.length > 0
      ? 'Methodological restraint recommended — quality over quantity.'
      : 'Pipeline is appropriately scoped for this domain and sample.',
  }
}

/**
 * Check whether a set of selected analyses is methodologically defensible.
 * Used for real-time validation in the Research Lab UI.
 */
export function auditSelectedAnalyses(selected, config) {
  const recommendation = recommendPipeline(config)
  const notRecommended = selected.filter(a => !recommendation.recommendedAnalyses.includes(a))
  const missing = recommendation.recommendedAnalyses.filter(a => !selected.includes(a) && ANALYSIS_RULES[a]?.alwaysInclude)

  return {
    isDefensible: notRecommended.length === 0 && missing.length === 0,
    notRecommended: notRecommended.map(a => ({
      analysis: a,
      reason: recommendation.exclusionWarnings.find(w => w.analysis === a)?.reason || 'Not recommended for this configuration.',
    })),
    missing: missing.map(a => ({ analysis: a, reason: `${ANALYSIS_RULES[a]?.label} is always recommended.` })),
    overproductionWarnings: recommendation.overproductionWarnings,
  }
}
